import React, { useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Shield,
  UserPlus,
  KeyRound,
  Power,
  PowerOff,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [newPerms, setNewPerms] = useState({});
  const [creating, setCreating] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [editPassword, setEditPassword] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const [deleteUser, setDeleteUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/users"),
        api.get("/users/permissions-catalog"),
      ]);
      setUsers(u.data);
      setCatalog(c.data);
      if (Object.keys(newPerms).length === 0) {
        const init = {};
        c.data.forEach((p) => {
          init[p.key] = ["view_dashboard", "view_sales"].includes(p.key);
        });
        setNewPerms(init);
      }
    } catch (err) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const createUser = async (e) => {
    e.preventDefault();
    if (newUsername.length < 3 || newPassword.length < 6) {
      toast.error("Usuário >=3 caracteres e senha >=6");
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", {
        username: newUsername.trim(),
        password: newPassword,
        is_admin: newIsAdmin,
        permissions: newPerms,
      });
      toast.success("Usuário criado");
      setCreateOpen(false);
      setNewUsername("");
      setNewPassword("");
      setNewIsAdmin(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar usuário");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditPerms({ ...u.permissions });
    setEditIsAdmin(!!u.is_admin);
    setEditActive(u.active !== false);
    setEditPassword("");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        permissions: editPerms,
        is_admin: editIsAdmin,
        active: editActive,
      };
      if (editPassword) payload.password = editPassword;
      await api.put(`/users/${editing.username}`, payload);
      toast.success("Usuário atualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteUser.username}`);
      toast.success("Usuário removido");
      setDeleteUser(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover");
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="users-tab-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="font-display">Usuários & Permissões</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Apenas administradores podem gerenciar usuários. Defina permissões específicas para cada função do painel.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="create-user-btn"
          id="new-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Novo usuário
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-2" data-testid="users-list">
            {users.map((u) => {
              const enabledCount = Object.values(u.permissions || {}).filter(Boolean).length;
              return (
                <div
                  key={u.username}
                  className="border border-border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  data-testid={`user-row-${u.username}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                      {u.username[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{u.username}</p>
                        {u.is_admin && (
                          <Badge variant="outline" className="text-[10px] border-primary/40 text-primary bg-primary/10">
                            <Shield className="w-3 h-3 mr-1" />
                            ADMIN
                          </Badge>
                        )}
                        {!u.active && (
                          <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-400 bg-red-500/10">
                            DESATIVADO
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {u.is_admin ? "Acesso total" : `${enabledCount} permissão(ões) ativa(s)`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteUser(u)}
                      className="text-destructive border-destructive/40 hover:bg-destructive/10"
                      data-testid={`delete-user-${u.username}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-border max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">Criar novo usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Usuário</Label>
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="bg-secondary/40"
                  data-testid="new-user-username"
                  required
                  minLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Senha</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-secondary/40"
                  data-testid="new-user-password"
                  required
                  minLength={6}
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30">
              <div>
                <p className="text-sm font-medium">Administrador</p>
                <p className="text-xs text-muted-foreground">Acesso total ao sistema</p>
              </div>
              <Switch checked={newIsAdmin} onCheckedChange={setNewIsAdmin} data-testid="new-user-admin-switch" />
            </div>
            {!newIsAdmin && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Permissões</Label>
                <div className="space-y-2 max-h-60 overflow-auto pr-1">
                  {catalog.map((p) => (
                    <label
                      key={p.key}
                      className="flex items-center justify-between p-2 rounded-md bg-secondary/20 border border-border cursor-pointer"
                    >
                      <span className="text-sm">{p.label}</span>
                      <Switch
                        checked={!!newPerms[p.key]}
                        onCheckedChange={(v) =>
                          setNewPerms((prev) => ({ ...prev, [p.key]: v }))
                        }
                        data-testid={`new-perm-${p.key}`}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="create-user-submit"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar usuário"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-card border-border max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Editar {editing?.username}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">
                  Nova senha (deixe em branco para manter)
                </Label>
                <Input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  className="bg-secondary/40"
                  data-testid="edit-user-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <p className="text-sm font-medium">Admin</p>
                  </div>
                  <Switch checked={editIsAdmin} onCheckedChange={setEditIsAdmin} data-testid="edit-admin-switch" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30">
                  <div className="flex items-center gap-2">
                    {editActive ? <Power className="w-4 h-4 text-primary" /> : <PowerOff className="w-4 h-4 text-muted-foreground" />}
                    <p className="text-sm font-medium">Ativo</p>
                  </div>
                  <Switch checked={editActive} onCheckedChange={setEditActive} data-testid="edit-active-switch" />
                </div>
              </div>
              {!editIsAdmin && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Permissões</Label>
                  <div className="space-y-2 max-h-60 overflow-auto pr-1">
                    {catalog.map((p) => (
                      <label
                        key={p.key}
                        className="flex items-center justify-between p-2 rounded-md bg-secondary/20 border border-border cursor-pointer"
                      >
                        <span className="text-sm">{p.label}</span>
                        <Switch
                          checked={!!editPerms[p.key]}
                          onCheckedChange={(v) =>
                            setEditPerms((prev) => ({ ...prev, [p.key]: v }))
                          }
                          data-testid={`edit-perm-${p.key}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={saveEdit}
                  disabled={saving}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="edit-user-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar alterações"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação é permanente. O usuário {deleteUser?.username} perderá imediatamente o acesso ao painel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
